/**
 * @fileoverview Dataset template detail slide-in panel for the Datasets grid.
 * @module system-web/components/migration/DatasetDetailSlideIn
 *
 * Responsibilities:
 * - Render a four-tab detail view for a selected migration dataset template.
 * - Provide Activate / Deactivate action buttons in the panel header.
 * - Display Overview, Tables, Mappings, and Relationships tabs.
 *
 * Dependencies:
 * - lucide-react for icons.
 * - MigrationDatasetTemplate type from @firesquirrel/legacy-migration.
 *
 * Architecture:
 * - Presentation-only: all data supplied by parent via props from useDatasetTemplates.
 * - No direct API calls; no direct Dexie access.
 * - Exported as a standalone body component + a separate header component so they can be
 *   wired into GridPageShell's `panelContent` and `panelHeader` props respectively.
 *
 * Notes:
 * - Tabs: Overview | Tables | Mappings | Relationships
 * - Activate/Deactivate button is in DatasetDetailSlideInHeader.
 *
 * @reusable
 * Usage: Rendered as GridPageShell panelContent in DatasetTemplateGrid.
 * Rules: presentation-only; receives all data via props.
 */

import React, { useState } from 'react';
import { CheckCircle2, XCircle, X, Layers, Activity, Loader2 } from 'lucide-react';
import type { MigrationDatasetTemplate, FieldMapping } from '../../types/legacyMigrationTypes';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract DatasetDetailSlideInProps
 *
 * Purpose: Props for the four-tab dataset detail slide-in body.
 *
 * Fields:
 * - template: The selected MigrationDatasetTemplate (never null when rendered).
 *
 * Rules:
 * - template must come from the useDatasetTemplates hook via parent.
 * - No API calls from this component.
 *
 * Sync:
 * - All data sourced from useDatasetTemplates via parent component props.
 */
export interface DatasetDetailSlideInProps {
  template: MigrationDatasetTemplate;
}

/**
 * @contract DatasetDetailSlideInHeaderProps
 *
 * Purpose: Props for the slide-in panel header, including status badge, action button, and close.
 *
 * Fields:
 * - template: The selected template.
 * - onActivate: Called when the Activate button is clicked.
 * - onDeactivate: Called when the Deactivate button is clicked.
 * - onClose: Called when the panel close (X) button is clicked.
 * - isMutating: True when an activate/deactivate call is in-flight; disables action button.
 *
 * Sync:
 * - All data sourced from useDatasetTemplates via parent props.
 */
export interface DatasetDetailSlideInHeaderProps {
  template: MigrationDatasetTemplate;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onClose: () => void;
  isMutating: boolean;
}

// ---------------------------------------------------------------------------
// Header — exported for GridPageShell panelHeader prop
// ---------------------------------------------------------------------------

/**
 * @component DatasetDetailSlideInHeader
 * Purpose:     Panel header bar showing template name, status badge, and Activate/Deactivate button.
 * Props:       DatasetDetailSlideInHeaderProps
 * Behavior:    Activate/Deactivate toggles based on current status.
 * Data Source: local (props from useDatasetTemplates via DatasetTemplateGrid)
 * Constraints: no direct API calls; no fetch in useEffect
 * Performance: lightweight — no lists or heavy rendering
 */
export function DatasetDetailSlideInHeader({
  template,
  onActivate,
  onDeactivate,
  onClose,
  isMutating,
}: DatasetDetailSlideInHeaderProps) {
  const isActive = template.status === 'active';

  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 bg-white">
      <div className="flex items-center gap-2 min-w-0">
        <Layers className="h-4 w-4 text-gray-400 shrink-0" />
        <span className="font-semibold text-gray-900 truncate text-sm">{template.displayName}</span>
        <StatusBadge status={template.status} />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isActive ? (
          <button
            onClick={() => onDeactivate(template._id)}
            disabled={isMutating}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {isMutating ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
            Deactivate
          </button>
        ) : (
          <button
            onClick={() => onActivate(template._id)}
            disabled={isMutating}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {isMutating ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            Activate
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
          aria-label="Close panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'draft' | 'active' }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <Activity className="h-2.5 w-2.5" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      Draft
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type DatasetDetailTab = 'overview' | 'tables' | 'mappings' | 'relationships';

// ---------------------------------------------------------------------------
// Slide-in body
// ---------------------------------------------------------------------------

/**
 * @component DatasetDetailSlideIn
 * Purpose:     Four-tab detail view for a selected MigrationDatasetTemplate.
 * Props:       DatasetDetailSlideInProps
 * Behavior:    Tabs: Overview, Tables, Mappings, Relationships. Data is pure-prop rendering.
 * Data Source: local (props from useDatasetTemplates via DatasetTemplateGrid)
 * Constraints: no direct API calls; no fetch in useEffect
 * Performance: renders from already-loaded template document — no lazy fetches
 */
export function DatasetDetailSlideIn({ template }: DatasetDetailSlideInProps) {
  const [activeTab, setActiveTab] = useState<DatasetDetailTab>('overview');

  const tabs: { id: DatasetDetailTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'tables', label: 'Tables' },
    { id: 'mappings', label: 'Mappings' },
    { id: 'relationships', label: 'Relationships' },
  ];

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Tab bar */}
      <div className="flex gap-0 border-b border-gray-200 px-5 bg-white shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {activeTab === 'overview' && <OverviewTab template={template} />}
        {activeTab === 'tables' && <TablesTab template={template} />}
        {activeTab === 'mappings' && <MappingsTab template={template} />}
        {activeTab === 'relationships' && <RelationshipsTab template={template} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ template }: { template: MigrationDatasetTemplate }) {
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Key', value: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{template.key}</code> },
    { label: 'Description', value: template.description || <span className="text-gray-400 italic">None</span> },
    { label: 'Kind', value: template.kind === 'system-known' ? 'System-known' : 'User-defined' },
    { label: 'Created By', value: template.createdBy },
    { label: 'Created At', value: new Date(template.createdAt).toLocaleString() },
    { label: 'Last Updated', value: new Date(template.updatedAt).toLocaleString() },
    { label: 'Version', value: String(template.version) },
    {
      label: 'AI Sampled',
      value: template.aiMetadata ? (
        <span className="text-green-700">
          Yes — {new Date(template.aiMetadata.sampledAt).toLocaleDateString()}
          {' '}({template.aiMetadata.sampleSize} rows)
        </span>
      ) : (
        <span className="text-gray-400">No</span>
      ),
    },
  ];

  const childAdapterKeys = template.composite?.childAdapterKeys ?? [];

  return (
    <div className="space-y-5">
      <dl className="divide-y divide-gray-100">
        {rows.map(({ label, value }) => (
          <div key={label} className="py-2 flex gap-4">
            <dt className="w-32 shrink-0 text-xs text-gray-500 font-medium pt-0.5">{label}</dt>
            <dd className="text-xs text-gray-900 min-w-0">{value}</dd>
          </div>
        ))}
      </dl>
      {childAdapterKeys.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-700 mb-2">Composite Dependencies</h4>
          <ul className="space-y-1">
            {childAdapterKeys.map((key) => (
              <li key={key} className="text-xs text-gray-700 bg-gray-50 px-2.5 py-1.5 rounded">
                <code>{key}</code>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tables tab
// ---------------------------------------------------------------------------

function TablesTab({ template }: { template: MigrationDatasetTemplate }) {
  const allTables = [
    { ...template.source.primaryTable, role: 'Primary' as const },
    ...template.source.childTables.map((t) => ({ ...t, role: 'Child' as const })),
  ];

  return (
    <div className="space-y-4">
      {allTables.map((tbl) => (
        <div key={tbl.name} className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-800">{tbl.schema ? `${tbl.schema}.${tbl.name}` : tbl.name}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
              tbl.role === 'Primary' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
            }`}>
              {tbl.role}
            </span>
          </div>
          {tbl.columns.length > 0 && (
            <div className="divide-y divide-gray-100">
              {tbl.columns.map((col) => (
                <div key={col} className="px-3 py-1.5 text-xs text-gray-700 font-mono">{col}</div>
              ))}
            </div>
          )}
          {tbl.filters && tbl.filters.length > 0 && (
            <div className="border-t border-gray-200 px-3 py-2 space-y-1">
              <span className="text-xs font-medium text-gray-500">Filters</span>
              {tbl.filters.map((f, i) => (
                <div key={i} className="text-xs text-gray-700 bg-yellow-50 px-2 py-1 rounded">
                  <code>{f.column} {f.operator} {String(f.value)}</code>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {allTables.length === 0 && (
        <p className="text-xs text-gray-400 italic">No source tables configured.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mappings tab
// ---------------------------------------------------------------------------

function MappingsTab({ template }: { template: MigrationDatasetTemplate }) {
  const mappings: FieldMapping[] = template.fieldMappings ?? [];

  if (template.kind === 'system-known') {
    return (
      <div className="text-xs text-gray-500 italic py-2">
        System-known adapter — transform is built-in, no declarative field mappings.
      </div>
    );
  }

  if (mappings.length === 0) {
    return <p className="text-xs text-gray-400 italic">No field mappings defined.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left pb-2 pr-4 text-gray-500 font-medium">Source</th>
            <th className="text-left pb-2 pr-4 text-gray-500 font-medium">Target</th>
            {mappings.some((m) => m.transform) && (
              <th className="text-left pb-2 text-gray-500 font-medium">Transform</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {mappings.map((m, i) => (
            <tr key={i}>
              <td className="py-1.5 pr-4 font-mono text-gray-800">
                {m.sourceTable}.{m.sourceColumn}
              </td>
              <td className="py-1.5 pr-4 font-mono text-gray-800">
                {m.targetCollection}.{m.targetField}
              </td>
              {mappings.some((mp) => mp.transform) && (
                <td className="py-1.5 text-gray-500">
                  {m.transform ?? <span className="italic">none</span>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Relationships tab
// ---------------------------------------------------------------------------

function RelationshipsTab({ template }: { template: MigrationDatasetTemplate }) {
  const rels = template.source.relationships ?? [];

  if (rels.length === 0) {
    return <p className="text-xs text-gray-400 italic">No relationships declared.</p>;
  }

  return (
    <div className="space-y-3">
      {rels.map((rel, i) => (
        <div key={i} className="border border-gray-200 rounded-lg px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-semibold text-gray-800">
              {rel.parentTable}.{rel.parentColumn}
            </span>
            <span className="text-gray-400 text-xs">→</span>
            <span className="text-xs font-mono font-semibold text-gray-800">
              {rel.childTable}.{rel.childColumn}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            <span>Cardinality: <span className="text-gray-800">{rel.cardinality}</span></span>
            <span>Orphan policy: <span className={`font-medium ${rel.orphanPolicy === 'block' ? 'text-red-600' : 'text-yellow-700'}`}>{rel.orphanPolicy}</span></span>
          </div>
          {rel.orphanJustification && (
            <p className="text-xs text-gray-600 italic">{rel.orphanJustification}</p>
          )}
        </div>
      ))}
    </div>
  );
}
